import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Organization, Domain } from 'models';

import { useForm } from '@weezevent/weezjs-core';
import { Modal, Button, SelectHierarchyLabel, ToastContext, Icon, Input, InputLabel, FieldErrors } from '@weezevent/nacre';

import { LoadingOptions } from 'components';
import { checkIsDomainName } from 'utils/utils';

import css from '../domains.module.css';

export const DomainCreationModal = ({ open, onClose }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [search, setSearch] = React.useState('');
    const [domainError, setDomainError] = React.useState(false);

    const [form, setForm] = useForm({
        name: '',
        organizerId: null
    });

    const [organizations, loadingOrganizations] = Organization.useApiModel(
        {
            cache: false,
            allow_cache: false,
            query: { search, limit: 10, offset: 0 },
            launch: open
        },
        [search, open]
    );

    const organizationsOptions = React.useMemo(() => {
        if (loadingOrganizations) return LoadingOptions;
        if (!organizations) return [];

        return organizations.map(org => org.toOption());
    }, [organizations, loadingOrganizations]);

    // No need for callbacks here
    const handleDomain = value => {
        if (value) {
            try {
                checkIsDomainName(value);
                setDomainError(false);
            } catch (err) {
                setDomainError(true);
            }
        }
        setForm('name', value);
    };

    const handleSubmit = ({ redirect = false }) => {
        let domain = new Domain({ ...form });

        return domain
            .save({ organizationId: form.organizerId })
            .then(response => {
                if (response.status === 201) {
                    toast.success(t('sauron.toasts.domains.add-domain-success'));
                }
            })
            .catch(() => {
                toast.error(t('sauron.toasts.error'));
            })
            .finally(() => (redirect ? history.push(`domains/${domain.name}`) : onClose()));
    };

    return (
        <Modal
            className={css['modal-container']}
            size={'large'}
            open={open}
            allowExternalClosing={true}
            onClose={onClose}
            title={t('sauron.emails.domains.add-domain')}
            subtitle={t('sauron.emails.domains.add-domain-subtitle')}
            customFooter={
                <Button.Group>
                    <Button onClick={onClose} label={t('common.cta.cancel')} />
                    <Button primary inverted onClick={handleSubmit} disabled={!form.name || domainError} label={t('common.cta.save')} />
                    <Button primary onClick={() => handleSubmit({ redirect: true })} disabled={!form.name || domainError} label={t('sauron.emails.domains.save-and-configure')} />
                </Button.Group>
            }
        >
            <>
                <InputLabel className={css['input-label']} label={t('sauron.emails.domains.domain-name')} />
                <div className={css['input-wrapper']}>
                    <Input
                        errors={domainError}
                        leftIcon={<Icon name={'at'} size={'huge'} />}
                        hideIcons={true}
                        onChange={({ target: { value } }) => handleDomain(value)}
                        placeholder={'domain.com'}
                        value={form.name}
                    />
                </div>
                {domainError && <FieldErrors errors={[t('sauron.emails.domains.invalid-domain')]} />}

                <SelectHierarchyLabel
                    searchable
                    value={form.organizerId}
                    options={organizationsOptions}
                    externalOptions={organizationsOptions}
                    onChange={value => setForm('organizerId', value)}
                    label={t('common.organization')}
                    optionalText={t('common.optional')}
                    placeholder={t('common.select')}
                    placeholderSearch={t('common.search')}
                    searchValue={search}
                    onExternalSearch={value => setSearch(value)}
                />
            </>
        </Modal>
    );
};
